/**
 * Admin Item Form Page
 * Create and edit menu items
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Languages } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ImageUpload, type UploadedImage } from '@/components/common/ImageUpload';
import { TranslateDialog } from '@/components/common/TranslateDialog';
import { uploadService } from '@/services/uploadService';
import { itemService } from '@/services/itemService';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AdminPageShell, PageHeader, PageLoading, AdminButton } from '@/components/admin/shell';
import type { CreateItemRequest, UpdateItemRequest, ItemPhoto } from '@/types/item';

// Available categories for items
const ITEM_CATEGORIES = [
  'Entrantes',
  'Principales',
  'Postres',
  'Bebidas',
  'Vinos',
  'Cócteles',
  'Cafés',
  'Otros',
] as const;

// Validation schema
const itemSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(255),
  description: z.string().max(280, 'La descripción no puede exceder 280 caracteres').optional(),
  category: z.string().optional(),
  active: z.boolean().default(true),
  display_order: z.coerce.number().int().optional().nullable(),
  remote_item_id: z.string().optional(),
});

type ItemFormValues = z.infer<typeof itemSchema>;

export function ItemFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackAdminItem } = useAnalytics();
  const isEditing = id !== 'new';
  const lookupRemote = searchParams.get('lookup') === 'remote';

  // Image upload state
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [originalImages, setOriginalImages] = useState<UploadedImage[]>([]);
  const [photosToDelete, setPhotosToDelete] = useState<number[]>([]);
  const [isCropping, setIsCropping] = useState(false);
  const [translations, setTranslations] = useState<{ name: string; description: string }>({
    name: '',
    description: '',
  });
  const [translateField, setTranslateField] = useState<'name' | 'description' | null>(null);

  // Fetch item if editing
  const { data: item, isLoading } = useQuery({
    queryKey: ['admin-item', id, lookupRemote],
    queryFn: () => itemService.getItem(id!, lookupRemote ? 'remote' : undefined),
    enabled: isEditing,
  });

  const itemInternalId = lookupRemote ? item?.id : id;

  const form = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      active: true,
      display_order: null,
      remote_item_id: '',
    },
  });

  // Load item data into form
  useEffect(() => {
    if (item) {
      form.reset({
        name: item.name,
        description: item.description || '',
        category: item.category || '',
        active: item.active,
        display_order: item.display_order,
        remote_item_id: item.remote_item_id || '',
      });

      // Load existing photos with crop metadata
      const loadedImages = item.photos.map((photo: ItemPhoto) => ({
        id: photo.id,
        url: photo.url,
        originalUrl: photo.original_url,
        filename: photo.filename,
        attachmentId: photo.crop_metadata?.id,
        cropData: photo.crop_metadata?.crop_data || undefined,
        viewportPosition: photo.crop_metadata?.viewport_position || undefined,
        optimizationStatus: (photo.crop_metadata?.optimization_status as 'red' | 'yellow' | 'green' | 'error') || 'red',
        processedUrl: photo.processed_url,
        enhancedUrl: photo.enhanced_url,
        enhancementEnabled: photo.enhancement_enabled,
        mediaType: photo.content_type?.startsWith('video/') ? 'video' : 'image',
      }));
      setImages(loadedImages);
      setOriginalImages(loadedImages);
      setTranslations({
        name: item.translations?.en?.name || '',
        description: item.translations?.en?.description || '',
      });
    }
  }, [item, form]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateItemRequest) => itemService.createItem(data),
    onSuccess: (newItem) => {
      queryClient.invalidateQueries({ queryKey: ['admin-items'] });
      trackAdminItem.create(newItem.id);
      toast.success('Item creado correctamente');
      navigate('/admin/items');
    },
    onError: () => {
      toast.error('Error al crear el item');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateItemRequest }) =>
      itemService.updateItem(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-items'] });
      queryClient.invalidateQueries({ queryKey: ['admin-item', id] });
      trackAdminItem.edit(id);
      toast.success('Item actualizado correctamente');
      navigate('/admin/items');
    },
    onError: () => {
      toast.error('Error al actualizar el item');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => itemService.deleteItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-items'] });
      toast.success('Item eliminado correctamente');
      navigate('/admin/items');
    },
    onError: () => {
      toast.error('Error al eliminar el item');
    },
  });

  const hasUnsavedChanges = () => {
    if (form.formState.isDirty) return true;
    if (photosToDelete.length > 0) return true;
    if (images.length !== originalImages.length) return true;
    const originalIds = originalImages.map(img => img.id).sort();
    const currentIds = images.map(img => img.id).filter(Boolean).sort();
    return JSON.stringify(originalIds) !== JSON.stringify(currentIds);
  };

  /**
   * Handle image changes
   */
  const handleImagesChange = useCallback(
    (
      newImagesOrUpdater: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])
    ) => {
      setImages((prevImages) => {
        // Resolve the new images value
        const newImages =
          typeof newImagesOrUpdater === 'function'
            ? newImagesOrUpdater(prevImages)
            : newImagesOrUpdater;

        // Track deleted photos (only existing ones with IDs)
        const deletedPhotoIds = prevImages
          .filter((img) => img.id && !newImages.find((newImg) => newImg.id === img.id))
          .map((img) => img.id!);

        if (deletedPhotoIds.length > 0) {
          setPhotosToDelete((prev) => [...prev, ...deletedPhotoIds]);
        }

        return newImages;
      });
    },
    []
  );

  /**
   * Handle form submission
   */
  const onSubmit = async (values: ItemFormValues) => {
    // Remove empty strings and convert to null
    const cleanedValues = {
      ...values,
      description: values.description || undefined,
      category: values.category || undefined,
      remote_item_id: values.remote_item_id || undefined,
    };

    // Build photo IDs array in current order (for reordering support)
    // - Existing photos: use attachment ID (number)
    // - New photos: use signed ID (string)
    const photoIds = images
      .map((img) => {
        if (img.id) {
          return img.id.toString();
        }
        return img.signedId;
      })
      .filter(Boolean) as string[];

    // Delete photos marked for deletion
    const itemInternalId = lookupRemote && item ? item.id : id;
    if (isEditing && itemInternalId && photosToDelete.length > 0) {
      try {
        await Promise.all(
          photosToDelete.map((photoId) =>
            uploadService.deletePhoto(itemInternalId, photoId)
          )
        );
        setPhotosToDelete([]);
      } catch (error) {
        console.error('Error deleting photos:', error);
        toast.error('Error al eliminar algunas fotos');
      }
    }

    // Prepare payload with photos at root level
    const payload: CreateItemRequest | UpdateItemRequest = {
      item: cleanedValues,
    };

    // Always send photos array for reordering support (even if empty)
    payload.photos = photoIds;

    if (translations.name || translations.description) {
      payload.translations = {
        en: {
          name: translations.name || undefined,
          description: translations.description || undefined,
        },
      };
    }

    if (isEditing && itemInternalId) {
      updateMutation.mutate({
        id: itemInternalId,
        data: payload as UpdateItemRequest,
      });
    } else {
      createMutation.mutate(payload as CreateItemRequest);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  if (isLoading && isEditing) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Editar Item" />
          <PageLoading variant="form" rows={5} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/items')}
            className="min-h-11 min-w-11"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeader
            title={isEditing ? 'Editar Item' : 'Nuevo Item'}
            subtitle={
              isEditing
                ? 'Actualiza la información del item'
                : 'Añade un nuevo item al menú'
            }
          />
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Información del Item</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Nombre *</FormLabel>
                          <button
                            type="button"
                            onClick={() => setTranslateField('name')}
                            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-brand-primary transition-colors"
                            title="Traducir al inglés"
                          >
                            <Languages className="h-3.5 w-3.5" />
                            EN{translations.name ? ' ✓' : ''}
                          </button>
                        </div>
                        <FormControl>
                          <Input placeholder="Ej: Pulpo a la gallega" className="min-h-11" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                   )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => {
                    const charCount = field.value?.length || 0;
                    const maxChars = 280;
                    const isNearLimit = charCount > 250;
                    const isOverLimit = charCount > maxChars;

                    return (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Descripción</FormLabel>
                          <button
                            type="button"
                            onClick={() => setTranslateField('description')}
                            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-brand-primary transition-colors"
                            title="Traducir al inglés"
                          >
                            <Languages className="h-3.5 w-3.5" />
                            EN{translations.description ? ' ✓' : ''}
                          </button>
                        </div>
                        <FormControl>
                          <textarea
                            placeholder="Ej: Arroz bomba, azafrán, pollo, conejo, judías verdes..."
                            className="w-full min-h-[100px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                            {...field}
                          />
                        </FormControl>
                        <div className="flex items-center justify-between text-xs">
                          <FormDescription>
                            Se mostrará en el video después de la foto del plato (opcional)
                          </FormDescription>
                          <span
                            className={`font-mono ${isOverLimit
                              ? 'text-red-600 font-semibold'
                              : isNearLimit
                                ? 'text-orange-600'
                                : 'text-gray-500'
                              }`}
                          >
                            {charCount}/{maxChars}
                          </span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                {/* Category */}
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoría</FormLabel>
                      <FormControl>
                         <select
                           className="w-full min-h-11 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                           {...field}
                         >
                          <option value="">Seleccionar categoría...</option>
                          {ITEM_CATEGORIES.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Display Order */}
                <FormField
                  control={form.control}
                  name="display_order"
                  render={({ field }) => (
                      <FormItem>
                       <FormLabel>Orden de visualización</FormLabel>
                       <FormControl>
                         <Input
                           type="number"
                           placeholder="0"
                           className="min-h-11"
                           {...field}
                           value={field.value ?? ''}
                           onChange={(e) => {
                             const value = e.target.value;
                             field.onChange(value === '' ? null : parseInt(value));
                           }}
                         />
                       </FormControl>
                      <FormDescription>
                        Orden en el que aparece en el menú (menor primero)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Remote Item ID */}
                <FormField
                  control={form.control}
                  name="remote_item_id"
                  render={({ field }) => (
                      <FormItem>
                       <FormLabel>ID del TPV</FormLabel>
                       <FormControl>
                         <Input placeholder="ID del item en el sistema TPV" className="min-h-11" {...field} />
                       </FormControl>
                      <FormDescription>
                        ID del item en el sistema de punto de venta (opcional)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Photos */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Fotos del Item</label>
                  <ImageUpload
                    images={images}
                    onImagesChange={handleImagesChange}
                    maxImages={5}
                    disabled={isSubmitting || isCropping}
                    itemId={isEditing ? id : undefined}
                    onCropStart={() => setIsCropping(true)}
                    onCropSuccess={() => setIsCropping(false)}
                  />
                  <p className="text-sm text-gray-500">
                    Sube fotos del item para que aparezcan en el menú. Usa el icono azul para optimizar cada foto.
                  </p>
                </div>

                {/* Active checkbox */}
                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Item activo</FormLabel>
                        <FormDescription>
                          Los items inactivos no se mostrarán en el menú
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                {/* Actions */}
                <div className="flex gap-4">
                  <AdminButton
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    loadingText="Guardando..."
                    className="flex-1 md:flex-initial"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {isEditing ? 'Actualizar Item' : 'Crear Item'}
                  </AdminButton>
                  <AdminButton
                    type="button"
                    variant="ghost"
                    onClick={() => navigate('/admin/items')}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </AdminButton>
                  {isEditing && itemInternalId && (
                    <AdminButton
                      type="button"
                      variant="danger"
                      onClick={() => {
                        if (hasUnsavedChanges()) {
                          if (window.confirm('Tienes cambios sin guardar. ¿Seguro que quieres eliminar este item?')) {
                            deleteMutation.mutate(itemInternalId);
                          }
                        } else {
                          deleteMutation.mutate(itemInternalId);
                        }
                      }}
                      isLoading={deleteMutation.isPending}
                      loadingText="Eliminando..."
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </AdminButton>
                  )}
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      <TranslateDialog
        open={translateField !== null}
        onOpenChange={(open) => !open && setTranslateField(null)}
        fieldLabel={translateField === 'name' ? 'nombre' : 'descripción'}
        spanishValue={translateField === 'name' ? form.watch('name') || '' : form.watch('description') || ''}
        englishValue={translateField === 'name' ? translations.name : translations.description}
        multiline={translateField === 'description'}
        onSave={(value) => {
          if (translateField) {
            setTranslations((prev) => ({ ...prev, [translateField]: value }));
          }
        }}
      />
    </AdminPageShell>
  );
}
